import { SupportSessionBanner } from '@/features/support-session/components/SupportSessionBanner'
import { useCallback, useEffect, useState } from 'react'
import { Outlet, useMatch } from 'react-router-dom'
import { PageMetadata } from '@/components/common/PageMetadata'
import { ErrorBoundary } from '@/components/common/ErrorBoundary'
import { useMe } from '@/features/settings/hooks/useMe'
import { useUIStore } from '@/stores/useUIStore'
import { useIsDesktop } from '@/hooks/useMediaQuery'
import { useGlobalShortcuts } from '@/hooks/useGlobalShortcuts'
import { Footer } from './Footer'
import { Header } from './Header'
import { Sidebar } from './Sidebar'
import { CommandPalette } from '@/features/command-palette/CommandPalette'
import { ShortcutsHelp } from '@/features/command-palette/ShortcutsHelp'
import { NoticeBanner } from '@/features/notices/NoticeBanner'
import { BillingBanner } from '@/features/billing/components/BillingBanner'
import { cn } from '@/lib/cn'

const SIDEBAR_WIDTH_EXPANDED = 'md:ml-56'
const SIDEBAR_WIDTH_COLLAPSED = 'md:ml-16'

/**
 * Shell layout for authenticated pages: sidebar, header, and main content area.
 * Triggers me query so permissions are available for Sidebar and route guards.
 * Hosts the global command palette (Cmd/Ctrl+K) and keyboard shortcuts.
 *
 * When viewing a specific board (/boards/:boardId), the sidebar and header
 * remain visible (ClickUp style) but main content padding is removed so the
 * board view fills the available space edge-to-edge.
 */
export function DashboardLayout(): React.ReactElement {
  useMe()
  const isSidebarOpen = useUIStore((s) => s.isSidebarOpen)
  const setSidebarOpen = useUIStore((s) => s.setSidebarOpen)
  const isDesktop = useIsDesktop()

  const [paletteOpen, setPaletteOpen] = useState(false)
  const [helpOpen, setHelpOpen] = useState(false)

  const togglePalette = useCallback(() => setPaletteOpen((o) => !o), [])
  const openHelp = useCallback(() => setHelpOpen(true), [])
  const openPalette = useCallback(() => setPaletteOpen(true), [])

  useGlobalShortcuts({ paletteOpen, togglePalette, openHelp })

  // Detect routes that need edge-to-edge, viewport-height content.
  const boardDetailMatch = useMatch('/boards/:boardId')
  const archivedBoardsMatch = useMatch('/boards/archived')
  const channelsMatch = useMatch('/channels')
  const isEdgeToEdge =
    (Boolean(boardDetailMatch) && !archivedBoardsMatch) || Boolean(channelsMatch)

  // Channels is a focused workspace: keep the main navigation compact while it
  // is active, then restore the full navigation when the user leaves it.
  useEffect(() => {
    if (!isDesktop) return
    setSidebarOpen(!channelsMatch)
  }, [channelsMatch, isDesktop, setSidebarOpen])


  const mainMarginClass = isDesktop
    ? isSidebarOpen
      ? SIDEBAR_WIDTH_EXPANDED
      : SIDEBAR_WIDTH_COLLAPSED
    : 'ml-0'

  return (
    <div
      className={cn(
        'flex bg-white dark:bg-gray-950',
        isEdgeToEdge ? 'h-screen overflow-hidden' : 'min-h-screen'
      )}
    >
      <PageMetadata />
      <Sidebar />
      <div
        className={cn(
          'flex min-w-0 flex-1 flex-col transition-[margin] duration-200 ease-out',
          isEdgeToEdge && 'h-screen overflow-hidden',
          mainMarginClass
        )}
      >
        <Header
          onOpenSearch={openPalette}
        />
        <SupportSessionBanner />
        <BillingBanner />
        <NoticeBanner />
        <main className={cn(
          'w-full min-w-0 min-h-0 flex-1',
          isEdgeToEdge ? 'h-full overflow-hidden p-0' : 'px-4 py-4 sm:px-5 lg:px-6'
        )}>
          {/* Page-level boundary: a crash inside a route keeps the sidebar and
              header interactive, so the user can navigate out of it. */}
          <ErrorBoundary size="page">
            <Outlet />
          </ErrorBoundary>
        </main>
        {!isEdgeToEdge && <Footer />}
      </div>
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
      <ShortcutsHelp open={helpOpen} onClose={() => setHelpOpen(false)} />
    </div>
  )
}

